import { useState } from "react";
import { Alert, Divider, Paper, Stack, Typography } from "@mui/material";
import { PaymentElement } from "@stripe/react-stripe-js";
import type { StripeError } from "@stripe/stripe-js";
import {
  CreateTeamFormData,
  computeTotal,
  formatMoneyShort,
  normalizeWebsite,
} from "../types";
import { LabelValueRow as Row } from "../../components/LabelValueRow";
import { PaymentMethodRequest } from "../../../stripe/PaymentMethodRequest";

interface Props {
  data: CreateTeamFormData;
  pricePerSeat: number;
  currency: string;
  /** Set after Stripe accepts payment, while we wait for the webhook to mint
   *  the firm in Firestore. UI shows a "setting up your team" hint when present. */
  pendingFirmId: string | null;
  /** Called once the card form has rendered, so the wizard can allow scrolling. */
  onPaymentReady?: () => void;
  /** Called if Stripe can't load the card form. */
  onPaymentLoadError?: (event: { error: StripeError }) => void;
}

export const CheckoutStep = ({
  data,
  pricePerSeat,
  currency,
  pendingFirmId,
  onPaymentReady,
  onPaymentLoadError,
}: Props) => {
  const yearly = data.interval === "yearly";
  const total = formatMoneyShort(computeTotal(data.seats, pricePerSeat), currency);
  // The request link only makes sense once there is a card form to look past.
  const [cardReady, setCardReady] = useState(false);

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        You'll be the team's admin. Invite members right after payment.
      </Typography>

      <Paper
        elevation={0}
        sx={{ p: "12px 16px", borderRadius: "14px", bgcolor: "#F7F7F9" }}
      >
        <Stack spacing={1}>
          <Row label="Team name" value={data.firmName} />
          <Row label="Company website" value={normalizeWebsite(data.domain)} />
          <Row
            label={`${data.seats} seats × ${formatMoneyShort(pricePerSeat, currency)} / ${yearly ? "year" : "month"}`}
            value={total}
          />
          <Divider sx={{ borderColor: "#E6E6EB" }} />
          <Row
            label={`Total, billed ${yearly ? "yearly" : "monthly"}`}
            value={`${total} / ${yearly ? "yr" : "mo"}`}
            emphasis
          />
        </Stack>
      </Paper>

      {/* Same options as the personal Pro checkout (CheckoutForm), so both card forms match. */}
      <PaymentElement
        id="payment-element"
        options={{
          layout: { type: "accordion", defaultCollapsed: false, radios: false, spacedAccordionItems: false },
          wallets: { googlePay: "never", applePay: "never" },
        }}
        onReady={() => {
          setCardReady(true);
          onPaymentReady?.();
        }}
        onLoadError={(event) => {
          setCardReady(false);
          onPaymentLoadError?.(event);
        }}
      />
      {/* Hidden until the card form loads, and once the card payment is accepted. */}
      {cardReady && !pendingFirmId && <PaymentMethodRequest checkout="team" currency={currency} interval={data.interval} />}

      {pendingFirmId && (
        <Alert severity="info" variant="outlined">
          Payment accepted. We're setting up your team workspace — this usually
          takes a few seconds.
        </Alert>
      )}
    </Stack>
  );
};
